import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getTrendingArticles } from '@/lib/api';
import { Flame, Eye } from 'lucide-react';

const routeToCategory = {
  '/': 'Check On Yo Peoples News Feed',
  '/allstories': 'Check On Yo Peoples News Feed',
  '/worldstar': 'Cincy Worldstar',
  '/check-your-claims': 'Check Your Claims',
};

const HotTopics = ({ theme = 'dark', period = 'week', heading = 'Hot This Week' }) => {
  const isLight = theme === 'light';
  const location = useLocation();
  const [articles, setArticles] = useState([]);

  const category = routeToCategory[location.pathname] || null;
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPage) return;

    const fetchTrending = async () => {
      try {
        // Try category-specific first
        if (category) {
          const res = await getTrendingArticles(category, 6, period);
          if (res.data && res.data.length > 0) {
            setArticles(res.data);
            return;
          }
        }
        // Fallback: top across all categories
        const res = await getTrendingArticles(null, 6, period);
        setArticles(res.data || []);
      } catch (e) {
        console.error('Failed to fetch trending:', e);
      }
    };
    fetchTrending();
  }, [category, isAdminPage, period]);

  if (isAdminPage || articles.length === 0) return null;

  return (
    <div
      className={`border-t py-4 ${isLight ? 'border-gray-200 bg-[#F9FAFB]' : 'border-[#222] bg-[#0a0a0a]'}`}
      data-testid={`hot-topics-${period}`}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-3">
          <Flame size={14} className="text-yellow-600" />
          <span className="text-xs font-bold text-yellow-600 uppercase tracking-wider">{heading}</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 sm:gap-3" data-testid={`hot-topics-grid-${period}`}>
          {articles.slice(0, 6).map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.slug || article.id}`}
              className="group"
              data-testid={`hot-topic-${article.id}`}
            >
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  isLight
                    ? 'bg-white border border-gray-200 hover:border-yellow-400 shadow-sm hover:shadow-md'
                    : 'bg-[#111] border border-[#222] hover:border-yellow-500/50'
                }`}
              >
                {article.featured_image && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-1 sm:p-2">
                  <h4 className={`text-[10px] sm:text-xs font-semibold leading-tight line-clamp-2 group-hover:text-yellow-600 transition-colors ${isLight ? 'text-gray-900' : 'text-white'}`}>
                    {article.title}
                  </h4>
                  <span className="mt-1 flex items-center gap-0.5 text-[8px] text-green-600 sm:text-[10px]">
                    <Eye size={8} />
                    {article.views || 0}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HotTopics;
